import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import animate from '@play-co/timestep-core/lib/animate';
import { ImageView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import CareActionButton, {
  CareActionButtonType,
  CareActionSelection,
} from './CareActionButton';
import StateObserver from 'src/StateObserver';
import {
  performCareActionSequence,
  purchaseCareItemSequence,
  purchaseGrowthCapsuleSequence,
} from '../care/care.sequence';
import * as Daycare from 'src/replicant/daycare';
import DaycareActionButton from '../daycare/DaycareActionButton';
import * as Pet from 'src/replicant/pet';
import { captureGenericError } from 'src/lib/sentry';
import { getPet } from '../pet';
import { getMutex } from 'src/replicant/utils/mutex';
import features from 'src/replicant/ruleset/features';
import * as Care from 'src/replicant/care';
import { PartialRecord } from 'src/replicant/utils/types';
import { registerHandTarget } from '../tutorial/services/TutorialHandService';
import { animDuration } from 'src/lib/utils';

const ui = uiConfig.buruburu.menu;

function getMenuHeight() {
  return ui.menu.height + 169;
}

export default class CareMenu extends FlexBoxImageScaleView {
  private mutex = getMutex();
  public buttons: PartialRecord<CareActionButtonType, CareActionButton> = {};

  constructor() {
    super({
      ...ui.menu,
      height: getMenuHeight(),
    });

    this.image.onInputStart = () => this.emit('Close');

    this.createMenu();

    const closeHint = this.addSubview(new ImageView(ui.closeHintBg));
    closeHint.canHandleEvents(false, true);
    getFlexNode(closeHint).updateStyle({ marginTop: 23 });
    closeHint.addSubview(
      new LangBitmapFontTextView({
        ...ui.closeHintText,
        localeText: () => i18n('footer.tapToClose'),
        width: closeHint.style.width,
        height: closeHint.style.height,
      }),
    );
  }

  /**
   * Default menu icons set
   */
  private createMenu() {
    // Add menu buttons
    const { onSelect } = this;
    let row = this.addSubview(new FlexBoxView(ui.menuRow));
    const { user } = StateObserver.getState();
    const now = StateObserver.now();

    const isAvailable = Daycare.getters.isAvailable(user, now);
    row.addSubview(new DaycareActionButton(isAvailable));
    this.buttons.growthcapsule = row.addSubview(
      new CareActionButton({
        type: 'growthcapsule',
        onSelect,
        isLocked: !!Care.getters.getGrowthCapsuleDisabledReason(user, now),
      }),
    );

    row = this.addSubview(new FlexBoxView(ui.menuRow));

    this.buttons.food = row.addSubview(
      new CareActionButton({ type: 'food', onSelect }),
    );
    registerHandTarget('foodCareMenuButton', this.buttons.food);
    this.buttons.snack = row.addSubview(
      new CareActionButton({ type: 'snack', onSelect }),
    );
    this.buttons.toy = row.addSubview(
      new CareActionButton({ type: 'toy', onSelect }),
    );
    registerHandTarget('toyCareMenuButton', this.buttons.toy);

    row = this.addSubview(new FlexBoxView(ui.menuRow));
    this.buttons.firstAid = row.addSubview(
      new CareActionButton({
        type: 'firstAid',
        onSelect,
      }),
    );
    registerHandTarget('firstAidButton', this.buttons.firstAid);
    this.buttons.bath = row.addSubview(
      new CareActionButton({ type: 'bath', onSelect }),
    );
    this.buttons.toilet = row.addSubview(
      new CareActionButton({ type: 'toilet', onSelect }),
    );
  }

  public fadeIn(superview: View) {
    animate(this)
      .clear()
      .now(() =>
        this.updateOpts({
          superview,
          visible: true,
          opacity: 0,
          offsetY: 100,
        }),
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  public fadeOut() {
    animate(this)
      .clear()
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.removeFromSuperview();
      });
  }

  private onSelect = async ({ careItemId, type }: CareActionSelection) => {
    if (this.mutex.isLocked()) return;
    await this.mutex.lock();

    try {
      this.emit('Close');

      // Menu actions is locked
      if (StateObserver.getState().ui.lockMenu) return;

      // Block if there is actions running right now
      if (getPet().conversation.inProgress()) return;

      // Check if there is no evolution
      if (
        !Pet.getters.getCurrentEvolution(StateObserver.getState().user)?.species
      ) {
        captureGenericError(
          'notEnoughBalancePurchaseCareItemSequence sequence warning',
          new Error('No evolution on notEnoughBalancePurchaseCareItemSequence'),
        );
        return;
      }

      // Perform care action or purchase item
      if (type === 'bath' || type === 'toilet') {
        await performCareActionSequence(type);
      } else if (type === 'growthcapsule') {
        await purchaseGrowthCapsuleSequence();
      } else if (careItemId) {
        await purchaseCareItemSequence(careItemId);
      }
    } finally {
      this.mutex.release();
    }
  };
}
